/*
 * The facts the legal surfaces state, in one place.
 *
 * The policy's substance was written against the live Tulink code — the mobile
 * app, the backend and this site — so every claim on `/privacy` is traceable to
 * something that is actually implemented. When a behaviour changes, change it
 * here and in the policy body together.
 */

/**
 * The published privacy and support contact. This is the address the backend's
 * own customer emails tell people to write to, so it is the one address a user
 * is already expected to recognise.
 */
export const SUPPORT_EMAIL = 'support@tulink.xyz';

/** How long an organisation invite can be accepted (backend INVITE_TTL_MS). */
export const INVITE_EXPIRY_DAYS = 7;

/** The third party that receives and forwards this site's form submissions. */
export const FORM_PROVIDER = {
  name: 'Web3Forms',
  privacyUrl: 'https://web3forms.com/privacy',
};

export const DELETE_ACCOUNT_SUCCESS_MESSAGE =
  'Request received. Check your email for the verification steps.';

/**
 * Dates shown at the top of the policy.
 *
 * `effective` and `updated` are what Google Play reads to decide whether the
 * policy is live. They move together whenever the policy's substance changes.
 */
export const POLICY_DATES = {
  effective: '2 October 2026',
  updated: '2 October 2026',
};

/**
 * The developer/product name shown here must match the entity named in the
 * Google Play listing. `Tulink` is the product and developer name used
 * throughout this site; no registered company details are recorded anywhere in
 * this project, so none are stated.
 */
export const PUBLISHING_ENTITY = 'Tulink';

/**
 * Every third party that receives personal data, taken from the app's and the
 * backend's real dependency lists rather than from a template. Each entry has
 * to say what the provider actually receives — Google Play compares this
 * against the Data safety declaration.
 */
export const SERVICE_PROVIDERS = [
  {
    name: 'Firebase Authentication (Google)',
    privacyUrl: 'https://firebase.google.com/support/privacy',
    role: 'Creates and verifies Tulink accounts. Receives your email address, sign-in credentials and the identifiers tied to your account.',
  },
  {
    name: 'Firebase Cloud Messaging (Google)',
    privacyUrl: 'https://firebase.google.com/support/privacy',
    role: 'Delivers push notifications to your device. Receives the notification and the messaging token that identifies your installation of the app.',
  },
  {
    name: 'Firebase Crashlytics (Google)',
    privacyUrl: 'https://firebase.google.com/support/privacy',
    role: 'Collects crash and error reports from release builds, including the device model, operating system version, app version and the state of the app when it stopped working.',
  },
  {
    name: 'Google Sign-In',
    privacyUrl: 'https://policies.google.com/privacy',
    role: 'Optional way to sign in. If you use it, Google confirms your identity to us and gives us the basic profile details you approve.',
  },
  {
    name: 'Sign in with Apple',
    privacyUrl: 'https://www.apple.com/legal/privacy/',
    role: 'Optional way to sign in. If you use it, Apple confirms your identity to us and gives us the email address you choose to share, which may be one of Apple’s private relay addresses.',
  },
  {
    name: 'Mapbox',
    privacyUrl: 'https://www.mapbox.com/legal/privacy',
    role: 'Draws the map and the route inside the app. Receives the map and location data needed to render what you are looking at.',
  },
  {
    name: 'Google Maps Platform and Places',
    privacyUrl: 'https://policies.google.com/privacy',
    role: 'Powers place search, address lookup, routes and distance calculations. Receives the text you search for and, where it is needed to return useful results, a location to search near or the coordinates of a route.',
  },
  {
    name: 'Resend',
    privacyUrl: 'https://resend.com/legal/privacy-policy',
    role: 'Sends account email such as verification, password resets and service notices. Receives your email address and the contents of those messages.',
  },
  {
    name: 'Web3Forms',
    privacyUrl: 'https://web3forms.com/privacy',
    role: 'Receives and forwards account deletion request forms from this website to the Tulink team by email.',
  },
  {
    name: 'Google Groups and Google Play',
    privacyUrl: 'https://policies.google.com/privacy',
    role: 'Run Tulink’s Android testing programme. When you join the Tulink Testers group and opt in on Google Play, Google shares your Google account email address with the Tulink team as a group member and tester.',
  },
  {
    name: 'Clerk',
    privacyUrl: 'https://clerk.com/legal/privacy',
    role: 'Signs in the operators of organisations that use the Tulink business dashboard. It handles those operator accounts, not traveller accounts on the mobile app.',
  },
];
